import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

/**
 * HelpContext — lets a page override the help key the footer help popup resolves.
 *
 * Default: the footer derives the key from the current route slug (currentPage).
 * Dynamic pages register a more specific identity:
 *   - DynamicModulePage      → 'module:<TableName>'   (engineering supporting docs etc.)
 *   - DataEntryModulePage    → 'dataentry:<slug>'
 * via useHelpKeyOverride(key), which clears itself on unmount.
 *
 * Keys map 1:1 to AppCfg_HelpContent.HelpKey rows in the admin DB.
 */
const HelpContext = createContext({ helpOverride: null, setHelpOverride: () => {} });

export const HelpProvider = ({ children }) => {
  const [helpOverride, setHelpOverride] = useState(null);
  const value = useMemo(() => ({ helpOverride, setHelpOverride }), [helpOverride]);
  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
};

export const useHelp = () => useContext(HelpContext);

/** Register a page-specific help key for as long as the component is mounted. */
export const useHelpKeyOverride = (key) => {
  const { setHelpOverride } = useHelp();
  useEffect(() => {
    if (!key) return undefined;
    setHelpOverride(key);
    return () => setHelpOverride(null);
  }, [key, setHelpOverride]);
};

export default HelpContext;
